/**
 * PriserPage - Pricing page
 *
 * SEO Target: /priser
 * Primary keywords: årsredovisning pris, årsredovisning kostnad, billig årsredovisning
 */

import { useState } from "react";
import { PageMeta, getBreadcrumbSchema, getProductSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";
import { AuthModal } from "@/components/auth/AuthModal";
import { usePricing } from "@/hooks/use-pricing";

const CheckIcon = () => (
  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

export function PriserPage() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { priceExclVat, priceInclVat, formattedPriceWithSuffix, formattedPriceExclVatFull } = usePricing();

  const breadcrumbItems = [{ label: "Priser" }];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Priser", url: "/priser" },
  ]);

  const productSchema = getProductSchema(priceExclVat);

  // Calculate 10% discount for Professional tier
  const discountedPrice = priceExclVat ? Math.round(priceExclVat * 0.9) : 2245;
  const formattedDiscountedPrice = new Intl.NumberFormat("sv-SE").format(discountedPrice);

  // Format the incl. moms value for FAQ
  const formattedInclVat = priceInclVat
    ? new Intl.NumberFormat("sv-SE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(priceInclVat)
    : "3 118,75";

  return (
    <>
      <PageMeta
        title={`Priser - Årsredovisning Online från ${formattedPriceWithSuffix} | Kvanten`}
        description={`Transparent prissättning för digital årsredovisning. ${formattedPriceExclVatFull} per företag/år. Gratis att testa, betala endast vid export. Jämför med byrå.`}
        keywords="årsredovisning pris, årsredovisning kostnad, billig årsredovisning, kvanten pris, årsredovisning online pris"
        canonicalUrl={`${SEO_CONFIG.siteUrl}/priser`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Enkel och transparent prissättning
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Ett pris per företag och räkenskapsår. Ingen prenumeration, inga dolda avgifter.
              Gratis att testa - du betalar endast när du exporterar din färdiga årsredovisning.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8 items-start">
            {/* Main pricing card */}
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl border-2 border-blue-500 p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <span className="text-sm font-medium text-blue-600 uppercase tracking-wide">
                    Komplett paket
                  </span>
                  <h2 className="text-3xl font-bold text-gray-900 mt-1">Årsredovisning</h2>
                </div>
                <div className="text-right">
                  <div className="text-5xl font-bold text-gray-900">{formattedPriceWithSuffix}</div>
                  <p className="text-gray-500">exkl. moms / företag / år</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Årsredovisning</h3>
                  <ul className="space-y-2">
                    {[
                      "K2 eller K3-årsredovisning",
                      "Automatisk SIE-filanalys",
                      "Alla obligatoriska noter",
                      "PDF enligt Bolagsverkets format",
                      "XML för digital inlämning",
                      "Automatisk K2/K3-validering",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckIcon />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Ingår utan extra kostnad</h3>
                  <ul className="space-y-2">
                    {[
                      "Digital aktiebok",
                      "Årsstämmoprotokoll",
                      "7 års arkivering",
                      "Obegränsad support",
                      "Digital signering",
                      "Exportera när du vill",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckIcon />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <button
                onClick={() => setShowAuthModal(true)}
                className="w-full inline-flex items-center justify-center px-6 py-4 text-lg font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Kom igång gratis
                <ArrowRightIcon />
              </button>
              <p className="text-center text-sm text-gray-500 mt-4">
                Gratis att skapa konto och testa. Du betalar först vid export.
              </p>
            </div>

            {/* Comparison card */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Jämfört med byrå</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <span className="text-gray-600">Traditionell byrå</span>
                  <span className="text-gray-900 font-semibold">8 000 - 25 000 kr</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <span className="text-gray-600">Kvanten</span>
                  <span className="text-blue-600 font-semibold">{formattedPriceWithSuffix}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Du sparar</span>
                  <span className="text-green-600 font-bold">upp till 90%</span>
                </div>
              </div>

              <div className="mt-6 p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>5 500 - 22 500 kr i besparingar</strong> per årsredovisning jämfört med
                  att anlita en traditionell redovisningsbyrå.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bureau pricing */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">För redovisningsbyråer</h2>
            <p className="text-xl text-gray-600">
              Volymrabatt för byråer som hanterar flera klienters årsredovisningar
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Starter",
                clients: "1-5 företag",
                price: formattedPriceWithSuffix,
                priceNote: "per företag",
                features: ["Ingen volymrabatt", "Standard support", "Grundfunktioner"],
              },
              {
                name: "Professional",
                clients: "6-20 företag",
                price: `${formattedDiscountedPrice} kr`,
                priceNote: "per företag (10% rabatt)",
                features: ["10% volymrabatt", "Prioriterad support", "Byrådashboard"],
                popular: true,
              },
              {
                name: "Enterprise",
                clients: "21+ företag",
                price: "Kontakta oss",
                priceNote: "för offert",
                features: [
                  "Anpassad prissättning",
                  "Dedikerad kontaktperson",
                  "API-åtkomst",
                  "SLA-garanti",
                ],
              },
            ].map((tier, i) => (
              <div
                key={i}
                className={`bg-white rounded-xl p-6 ${
                  tier.popular ? "ring-2 ring-blue-500 shadow-lg" : "border border-gray-200"
                }`}
              >
                {tier.popular && (
                  <div className="text-xs font-semibold text-blue-600 uppercase mb-2">
                    Populärast
                  </div>
                )}
                <h3 className="text-xl font-bold text-gray-900">{tier.name}</h3>
                <p className="text-sm text-gray-500 mb-4">{tier.clients}</p>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-gray-900">{tier.price}</span>
                  <span className="text-sm text-gray-500 block">{tier.priceNote}</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {tier.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckIcon />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setShowAuthModal(true)}
                  className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${
                    tier.popular
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {tier.price === "Kontakta oss" ? "Kontakta oss" : "Kom igång"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Vad ingår i priset?</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "SIE-filanalys",
                items: [
                  "Automatisk parsing av SIE4-filer",
                  "Validering av balansekvationen",
                  "Detektering av regelverksval (K2/K3)",
                ],
              },
              {
                title: "Årsredovisning",
                items: [
                  "Förvaltningsberättelse med flerårsöversikt",
                  "Resultaträkning och balansräkning",
                  "Alla obligatoriska noter",
                ],
              },
              {
                title: "Export & Inlämning",
                items: [
                  "PDF enligt Bolagsverkets format",
                  "XML för digital inlämning",
                  "Underlag för deklaration (INK2)",
                ],
              },
              {
                title: "Tilläggstjänster",
                items: [
                  "Digital aktiebok (ABL 5 kap)",
                  "Automatiskt årsstämmoprotokoll",
                  "7 års säker arkivering",
                ],
              },
            ].map((section, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4">{section.title}</h3>
                <ul className="space-y-2">
                  {section.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckIcon />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Vanliga frågor om priser
          </h2>

          <div className="space-y-6">
            {[
              {
                q: "Måste jag betala för att testa?",
                a: "Nej, det är helt gratis att skapa konto, ladda upp SIE-fil och se förhandsvisning av din årsredovisning. Du betalar först när du vill exportera den färdiga årsredovisningen.",
              },
              {
                q: "Vad händer om jag behöver göra ändringar efter export?",
                a: "Du kan göra obegränsade ändringar och ladda ner ny PDF under en 30-dagarsperiod efter första exporten. Därefter kan du fortfarande se och kopiera din årsredovisning.",
              },
              {
                q: "Tillkommer det moms?",
                a: `Ja, alla priser anges exklusive moms (25%). ${formattedPriceWithSuffix} exkl. moms = ${formattedInclVat} kr inkl. moms.`,
              },
              {
                q: "Kan jag få faktura?",
                a: "Ja, du kan välja mellan att betala med kort direkt eller få faktura med 30 dagars betalningsvillkor.",
              },
              {
                q: "Vad kostar det för K3?",
                a: "Samma pris oavsett om du väljer K2 eller K3. Kassaflödesanalys och uppskjuten skatt-beräkning ingår för K3 utan extra kostnad.",
              },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.q}</h3>
                <p className="text-gray-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Redo att komma igång?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Skapa konto gratis och ladda upp din SIE-fil. Du betalar endast vid export.
          </p>
          <button
            onClick={() => setShowAuthModal(true)}
            className="inline-flex items-center px-8 py-4 text-lg font-medium text-blue-600 bg-white rounded-lg hover:bg-blue-50 transition-colors"
          >
            Kom igång gratis
            <ArrowRightIcon />
          </button>
        </div>
      </section>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="register"
      />
    </>
  );
}
